import React, { useState, useEffect } from 'react';
import './ethewareSignUp.css'; 
import Navbar from '../navbar/navbar';
import ETWIcon from "../../assets/etheware-icon.png";
import { getAuth, createUserWithEmailAndPassword, sendEmailVerification, signInWithCustomToken, signInAnonymously  } from "firebase/auth";
import { getDatabase, ref, set } from "firebase/database";
import { initializeApp } from 'firebase/app';
import Swal from 'sweetalert2';
import { auth, database } from "../../Config/Firebase";

const EthewareSignUp = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [country, setCountry] = useState('US');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('+1');
  const [verificationMethod, setVerificationMethod] = useState('text');
  const [receiveAnnouncements, setReceiveAnnouncements] = useState(false);
  const [receiveAppsMusicTV, setReceiveAppsMusicTV] = useState(false);
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaImage, setCaptchaImage] = useState("CAPTCHA");
  

    // Generate initial CAPTCHA on component mount
  useEffect(() => {
    handleNewCode();
  }, []);


  const showAlert = (title, text, icon = 'success') => {
    Swal.fire({ title, text, icon });
  };

  const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);
  const isValidPassword = (password) => password.length >= 6;
  const isCaptchaFilled = (input) => input.trim() !== '';
  

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Ensure Firebase is initialized before proceeding
    if (!auth || !database) {
      showAlert('Setup Error', 'Firebase is not initialized. Please try again.', 'error');
      return;
    }

    if (!firstName || !lastName || !country || !birthMonth || !birthDay || !birthYear || !phoneNumber) {
      return showAlert('Missing Information', 'Please fill in all required fields.', 'warning');
    }

    if (!isValidEmail(email)) return showAlert('Invalid Email', 'Please enter a valid email.', 'warning');
    if (!isValidPassword(password)) return showAlert('Weak Password', 'Password must be at least 6 characters.', 'warning');
    if (captchaInput.toUpperCase() !== captchaImage.toUpperCase()) return showAlert('Invalid Captcha', 'The captcha you entered does not match.', 'warning');
    if (!isCaptchaFilled(captchaInput)) return showAlert('Captcha Required', 'Please complete the captcha.', 'warning');
    if (password !== confirmPassword) return showAlert('Password Mismatch', 'Passwords do not match.', 'warning');

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      console.log("User created:", userCredential.user.uid);

      // Send email verification
      await sendEmailVerification(userCredential.user);
      console.log("Email verification sent.");
      
      // Store user data in Realtime Database
      await set(ref(database, 'users/' + userCredential.user.uid), {
        firstName,
        lastName,
        country,
        birthDate: `${birthMonth}/${birthDay}/${birthYear}`,
        phoneNumber: `${phoneCountryCode}${phoneNumber}`, // Store full phone number
        verificationMethod,
        receiveAnnouncements,
        receiveAppsMusicTV,
        emailVerified: userCredential.user.emailVerified, // Initial status from Firebase Auth
        createdAt: new Date().toISOString(),
      });
        console.log("A new Etheware Account has been created.");
        showAlert('Welcome!', 'Your Etheware Account has been successfully created! Please verify your email to fully activate your account.', 'success');
        // Redirect user after successful signup
        window.location.href = 'https://www.es.etheware.com/';
    } catch (error) {
      console.error("Signup error:", error);
      showAlert('Error', error.message, 'error');
    }
  };

  const handleSignIn = (e) => {
    e.preventDefault(); // Prevent default link behavior
    window.location.href = '/sign-in';
  };

  const handleNewCode = () => {
    setCaptchaImage(Math.random().toString(36).substring(2, 9).toUpperCase());
  };

  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 126 }, (_, i) => currentYear - i);

  const countries = [
    { name: 'United States', code: 'US', phone: '+1' },
    { name: 'Afghanistan', code: 'AF', phone: '+93' },
    { name: 'Åland Islands', code: 'AX', phone: '+358' },
    { name: 'Albania', code: 'AL', phone: '+355' },
    { name: 'Algeria', code: 'DZ', phone: '+213' },
    { name: 'American Samoa', code: 'AS', phone: '+1' },
    { name: 'Andorra', code: 'AD', phone: '+376' },
    { name: 'Angola', code: 'AO', phone: '+244' },
    { name: 'Anguilla', code: 'AI', phone: '+1' },
    { name: 'Antarctica', code: 'AQ', phone: '+672' },
    { name: 'Antigua And Barbuda', code: 'AG', phone: '+1' },
    { name: 'Argentina', code: 'AR', phone: '+54' },
    { name: 'Armenia', code: 'AM', phone: '+374' },
    { name: 'Aruba', code: 'AW', phone: '+297' },
    { name: 'Australia', code: 'AU', phone: '+61' },
    { name: 'Austria', code: 'AT', phone: '+43' },
    { name: 'Azerbaijan', code: 'AZ', phone: '+994' },
    { name: 'Bahamas', code: 'BS', phone: '+1' },
    { name: 'Bahrain', code: 'BH', phone: '+973' },
    { name: 'Bangladesh', code: 'BD', phone: '+880' },
    { name: 'Barbados', code: 'BB', phone: '+1' },
    { name: 'Belarus', code: 'BY', phone: '+375' },
    { name: 'Belgium', code: 'BE', phone: '+32' },
    { name: 'Belize', code: 'BZ', phone: '+501' },
    { name: 'Benin', code: 'BJ', phone: '+229' },
    { name: 'Bermuda', code: 'BM', phone: '+1' },
    { name: 'Bhutan', code: 'BT', phone: '+975' },
    { name: 'Bolivia', code: 'BO', phone: '+591' },
    { name: 'Bosnia and Herzegovina', code: 'BA', phone: '+387' },
    { name: 'Botswana', code: 'BW', phone: '+267' },
    { name: 'Bouvet Island', code: 'BV', phone: '+47' },
    { name: 'Brazil', code: 'BR', phone: '+55' },
    { name: 'British Virgin Islands', code: 'VG', phone: '+1' },
    { name: 'Brunei Darussalam', code: 'BN', phone: '+673' },
    { name: 'Bulgaria', code: 'BG', phone: '+359' },
    { name: 'Burkina Faso', code: 'BF', phone: '+226' },
    { name: 'Burundi', code: 'BI', phone: '+257' },
    { name: 'Cambodia', code: 'KH', phone: '+855' },
    { name: 'Cameroon', code: 'CM', phone: '+237' },
    { name: 'Canada', code: 'CA', phone: '+1' },
    { name: 'Cape Verde', code: 'CV', phone: '+238' },
    { name: 'Caribbean Netherlands', code: 'BQ', phone: '+599' },
    { name: 'Cayman Islands', code: 'KY', phone: '+1' },
    { name: 'Central African Republic', code: 'CF', phone: '+236' },
    { name: 'Chad', code: 'TD', phone: '+235' },
    { name: 'Chagos Archipelago', code: 'IO', phone: '+246' },
    { name: 'Chile', code: 'CL', phone: '+56' },
    { name: 'China mainland', code: 'CN', phone: '+86' },
    { name: 'Christmas Island', code: 'CX', phone: '+61' },
    { name: 'Cocos (Keeling) Islands', code: 'CC', phone: '+61' },
    { name: 'Colombia', code: 'CO', phone: '+57' },
    { name: 'Comoros', code: 'KM', phone: '+269' },
    { name: 'Cook Islands', code: 'CK', phone: '+682' },
    { name: 'Costa Rica', code: 'CR', phone: '+506' },
    { name: 'Côte d\'Ivoire', code: 'CI', phone: '+225' },
    { name: 'Croatia', code: 'HR', phone: '+385' },
    { name: 'Curaçao', code: 'CW', phone: '+599' },
    { name: 'Cyprus', code: 'CY', phone: '+357' },
    { name: 'Czechia', code: 'CZ', phone: '+420' },
    { name: 'Democratic Republic of the Congo', code: 'CD', phone: '+243' },
    { name: 'Denmark', code: 'DK', phone: '+45' },
    { name: 'Djibouti', code: 'DJ', phone: '+253' },
    { name: 'Dominica', code: 'DM', phone: '+1' },
    { name: 'Dominican Republic', code: 'DO', phone: '+1' },
    { name: 'Ecuador', code: 'EC', phone: '+593' },
    { name: 'Egypt', code: 'EG', phone: '+20' },
    { name: 'El Salvador', code: 'SV', phone: '+503' },
    { name: 'Equatorial Guinea', code: 'GQ', phone: '+240' },
    { name: 'Eritrea', code: 'ER', phone: '+291' },
    { name: 'Estonia', code: 'EE', phone: '+372' },
    { name: 'Eswatini', code: 'SZ', phone: '+268' },
    { name: 'Ethiopia', code: 'ET', phone: '+251' },
    { name: 'Falkland Islands', code: 'FK', phone: '+500' },
    { name: 'Faroe Islands', code: 'FO', phone: '+298' },
    { name: 'Fiji', code: 'FJ', phone: '+679' },
    { name: 'Finland', code: 'FI', phone: '+358' },
    { name: 'France', code: 'FR', phone: '+33' },
    { name: 'French Guiana', code: 'GF', phone: '+594' },
    { name: 'French Polynesia', code: 'PF', phone: '+689' },
    { name: 'French Southern Territories', code: 'TF', phone: '+262' },
    { name: 'Gabon', code: 'GA', phone: '+241' },
    { name: 'Gambia', code: 'GM', phone: '+220' },
    { name: 'Georgia', code: 'GE', phone: '+995' },
    { name: 'Germany', code: 'DE', phone: '+49' },
    { name: 'Ghana', code: 'GH', phone: '+233' },
    { name: 'Gibraltar', code: 'GI', phone: '+350' },
    { name: 'Greece', code: 'GR', phone: '+30' },
    { name: 'Greenland', code: 'GL', phone: '+299' },
    { name: 'Grenada', code: 'GD', phone: '+1' },
    { name: 'Guadeloupe', code: 'GP', phone: '+590' },
    { name: 'Guam', code: 'GU', phone: '+1' },
    { name: 'Guatemala', code: 'GT', phone: '+502' },
    { name: 'Guernsey', code: 'GG', phone: '+44' },
    { name: 'Guinea', code: 'GN', phone: '+224' },
    { name: 'Guinea-Bissau', code: 'GW', phone: '+245' },
    { name: 'Guyana', code: 'GY', phone: '+592' },
    { name: 'Haiti', code: 'HT', phone: '+509' },
    { name: 'Heard And Mc Donald Islands', code: 'HM', phone: '+61' },
    { name: 'Honduras', code: 'HN', phone: '+504' },
    { name: 'Hong Kong', code: 'HK', phone: '+852' },
    { name: 'Hungary', code: 'HU', phone: '+36' },
    { name: 'Iceland', code: 'IS', phone: '+354' },
    { name: 'India', code: 'IN', phone: '+91' },
    { name: 'Indonesia', code: 'ID', phone: '+62' },
    { name: 'Iraq', code: 'IQ', phone: '+964' },
    { name: 'Ireland', code: 'IE', phone: '+353' },
    { name: 'Isle of Man', code: 'IM', phone: '+44' },
    { name: 'Israel', code: 'IL', phone: '+972' },
    { name: 'Italy', code: 'IT', phone: '+39' },
    { name: 'Jamaica', code: 'JM', phone: '+1' },
    { name: 'Japan', code: 'JP', phone: '+81' },
    { name: 'Jersey', code: 'JE', phone: '+44' },
    { name: 'Jordan', code: 'JO', phone: '+962' },
    { name: 'Kazakhstan', code: 'KZ', phone: '+7' },
    { name: 'Kenya', code: 'KE', phone: '+254' },
    { name: 'Kiribati', code: 'KI', phone: '+686' },
    { name: 'Kosovo', code: 'XK', phone: '+383' },
    { name: 'Kuwait', code: 'KW', phone: '+965' },
    { name: 'Kyrgyzstan', code: 'KG', phone: '+996' },
    { name: 'Laos', code: 'LA', phone: '+856' },
    { name: 'Latvia', code: 'LV', phone: '+371' },
    { name: 'Lebanon', code: 'LB', phone: '+961' },
    { name: 'Lesotho', code: 'LS', phone: '+266' },
    { name: 'Liberia', code: 'LR', phone: '+231' },
    { name: 'Libya', code: 'LY', phone: '+218' },
    { name: 'Liechtenstein', code: 'LI', phone: '+423' },
    { name: 'Lithuania', code: 'LT', phone: '+370' },
    { name: 'Luxembourg', code: 'LU', phone: '+352' },
    { name: 'Macao', code: 'MO', phone: '+853' },
    { name: 'Madagascar', code: 'MG', phone: '+261' },
    { name: 'Malawi', code: 'MW', phone: '+265' },
    { name: 'Malaysia', code: 'MY', phone: '+60' },
    { name: 'Maldives', code: 'MV', phone: '+960' },
    { name: 'Mali', code: 'ML', phone: '+223' },
    { name: 'Malta', code: 'MT', phone: '+356' },
    { name: 'Marshall Islands', code: 'MH', phone: '+692' },
    { name: 'Martinique', code: 'MQ', phone: '+596' },
    { name: 'Mauritania', code: 'MR', phone: '+222' },
    { name: 'Mauritius', code: 'MU', phone: '+230' },
    { name: 'Mayotte', code: 'YT', phone: '+262' },
    { name: 'Mexico', code: 'MX', phone: '+52' },
    { name: 'Micronesia', code: 'FM', phone: '+691' },
    { name: 'Moldova', code: 'MD', phone: '+373' },
    { name: 'Monaco', code: 'MC', phone: '+377' },
    { name: 'Mongolia', code: 'MN', phone: '+976' },
    { name: 'Montenegro', code: 'ME', phone: '+382' },
    { name: 'Montserrat', code: 'MS', phone: '+1' },
    { name: 'Morocco', code: 'MA', phone: '+212' },
    { name: 'Mozambique', code: 'MZ', phone: '+258' },
    { name: 'Myanmar', code: 'MM', phone: '+95' },
    { name: 'Namibia', code: 'NA', phone: '+264' },
    { name: 'Nauru', code: 'NR', phone: '+674' },
    { name: 'Nepal', code: 'NP', phone: '+977' },
    { name: 'Netherlands', code: 'NL', phone: '+31' },
    { name: 'New Caledonia', code: 'NC', phone: '+687' },
    { name: 'New Zealand', code: 'NZ', phone: '+64' },
    { name: 'Nicaragua', code: 'NI', phone: '+505' },
    { name: 'Niger', code: 'NE', phone: '+227' },
    { name: 'Nigeria', code: 'NG', phone: '+234' },
    { name: 'Niue', code: 'NU', phone: '+683' },
    { name: 'Norfolk Island', code: 'NF', phone: '+672' },
    { name: 'Northern Mariana Islands', code: 'MP', phone: '+1' },
    { name: 'North Macedonia', code: 'MK', phone: '+389' },
    { name: 'Norway', code: 'NO', phone: '+47' },
    { name: 'Oman', code: 'OM', phone: '+968' },
    { name: 'Pakistan', code: 'PK', phone: '+92' },
    { name: 'Palau', code: 'PW', phone: '+680' },
    { name: 'Palestinian Territories', code: 'PS', phone: '+970' },
    { name: 'Panama', code: 'PA', phone: '+507' },
    { name: 'Papua New Guinea', code: 'PG', phone: '+675' },
    { name: 'Paraguay', code: 'PY', phone: '+595' },
    { name: 'Peru', code: 'PE', phone: '+51' },
    { name: 'Philippines', code: 'PH', phone: '+63' },
    { name: 'Pitcairn', code: 'PN', phone: '+870' },
    { name: 'Poland', code: 'PL', phone: '+48' },
    { name: 'Portugal', code: 'PT', phone: '+351' },
    { name: 'Puerto Rico', code: 'PR', phone: '+1' },
    { name: 'Qatar', code: 'QA', phone: '+974' },
    { name: 'Republic of the Congo', code: 'CG', phone: '+242' },
    { name: 'Réunion', code: 'RE', phone: '+262' },
    { name: 'Romania', code: 'RO', phone: '+40' },
    { name: 'Russia', code: 'RU', phone: '+7' },
    { name: 'Rwanda', code: 'RW', phone: '+250' },
    { name: 'Saint Barthélemy', code: 'BL', phone: '+590' },
    { name: 'Saint Helena', code: 'SH', phone: '+290' },
    { name: 'Saint Kitts And Nevis', code: 'KN', phone: '+1' },
    { name: 'Saint Lucia', code: 'LC', phone: '+1' },
    { name: 'Saint Martin', code: 'MF', phone: '+1' },
    { name: 'Saint Vincent and the Grenadines', code: 'VC', phone: '+1' },
    { name: 'Samoa', code: 'WS', phone: '+685' },
    { name: 'San Marino', code: 'SM', phone: '+378' },
    { name: 'Sao Tome And Principe', code: 'ST', phone: '+239' },
    { name: 'Saudi Arabia', code: 'SA', phone: '+966' },
    { name: 'Senegal', code: 'SN', phone: '+221' },
    { name: 'Serbia', code: 'RS', phone: '+381' },
    { name: 'Seychelles', code: 'SC', phone: '+248' },
    { name: 'Sierra Leone', code: 'SL', phone: '+232' },
    { name: 'Singapore', code: 'SG', phone: '+65' },
    { name: 'Sint Maarten', code: 'SX', phone: '+1' },
    { name: 'Slovakia', code: 'SK', phone: '+421' },
    { name: 'Slovenia', code: 'SI', phone: '+386' },
    { name: 'Solomon Islands', code: 'SB', phone: '+677' },
    { name: 'Somalia', code: 'SO', phone: '+252' },
    { name: 'South Africa', code: 'ZA', phone: '+27' },
    { name: 'South Georgia and South Sandwich Islands', code: 'GS', phone: '+500' },
    { name: 'South Korea', code: 'KR', phone: '+82' },
    { name: 'South Sudan', code: 'SS', phone: '+211' },
    { name: 'Spain', code: 'ES', phone: '+34' },
    { name: 'Sri Lanka', code: 'LK', phone: '+94' },
    { name: 'St. Pierre And Miquelon', code: 'PM', phone: '+508' },
    { name: 'Sudan', code: 'SD', phone: '+249' },
    { name: 'Suriname', code: 'SR', phone: '+597' },
    { name: 'Svalbard And Jan Mayen Islands', code: 'SJ', phone: '+47' },
    { name: 'Sweden', code: 'SE', phone: '+46' },
    { name: 'Switzerland', code: 'CH', phone: '+41' },
    { name: 'Taiwan', code: 'TW', phone: '+886' },
    { name: 'Tajikistan', code: 'TJ', phone: '+992' },
    { name: 'Tanzania', code: 'TZ', phone: '+255' },
    { name: 'Thailand', code: 'TH', phone: '+66' },
    { name: 'Timor-Leste', code: 'TL', phone: '+670' },
    { name: 'Togo', code: 'TG', phone: '+228' },
    { name: 'Tokelau', code: 'TK', phone: '+690' },
    { name: 'Tonga', code: 'TO', phone: '+676' },
    { name: 'Trinidad and Tobago', code: 'TT', phone: '+1' },
    { name: 'Tunisia', code: 'TN', phone: '+216' },
    { name: 'Türkiye', code: 'TR', phone: '+90' },
    { name: 'Turkmenistan', code: 'TM', phone: '+993' },
    { name: 'Turks and Caicos Islands', code: 'TC', phone: '+1' },
    { name: 'Tuvalu', code: 'TV', phone: '+688' },
    { name: 'Uganda', code: 'UG', phone: '+256' },
    { name: 'Ukraine', code: 'UA', phone: '+380' },
    { name: 'United Arab Emirates', code: 'AE', phone: '+971' },
    { name: 'United Kingdom', code: 'GB', phone: '+44' },
    { name: 'United States Minor Outlying Islands', code: 'UM', phone: '+1' },
    { name: 'Uruguay', code: 'UY', phone: '+598' },
    { name: 'Uzbekistan', code: 'UZ', phone: '+998' },
    { name: 'Vanuatu', code: 'VU', phone: '+678' },
    { name: 'Vatican', code: 'VA', phone: '+39' },
    { name: 'Venezuela', code: 'VE', phone: '+58' },
    { name: 'Vietnam', code: 'VN', phone: '+84' },
    { name: 'Virgin Islands (U.S.)', code: 'VI', phone: '+1' },
    { name: 'Wallis And Futuna Islands', code: 'WF', phone: '+681' },
    { name: 'Western Sahara', code: 'EH', phone: '+212' },
    { name: 'Yemen', code: 'YE', phone: '+967' },
    { name: 'Zambia', code: 'ZM', phone: '+260' },
    { name: 'Zimbabwe', code: 'ZW', phone: '+263' },
  ];

  return (
    <>
      {/* Placeholder for Navbar */}
      <div style={{ width: '100%', height: '55px', background: '#000', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'absolute', top: 0, left: 0, zIndex: 9999 }}>
         <Navbar />
      </div>

      <div className="signup-container">
        <div className="etheware-logo-placeholder"><img src={ETWIcon} alt='Etheware Sign Up' width={50}/></div> {/* Using Apple logo for visual reference */}
        <div className="signup-header">
          <h1>Create An Etheware Account</h1>
          <p>Your Etheware Account is all you need to access all your Etheware services including Etheware Space, ES Manager, and more of our products.</p>
          <p>Already have an Etheware Account? <a href="sign-in" onClick={handleSignIn}>Sign In</a></p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="name-inputs">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-group country-select-wrapper">
            <label htmlFor="country">Country/Region</label>
            <select
              id="country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              required
            >
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Birthday</label>
            <div className="birthday-inputs">
              <div className="form-group">
                <select
                  id="birthMonth"
                  value={birthMonth}
                  onChange={(e) => setBirthMonth(e.target.value)}
                  required
                >
                  <option value="">Month</option>
                  {months.map((month, index) => (
                    <option key={index} value={month}>{month}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <select
                  id="birthDay"
                  value={birthDay}
                  onChange={(e) => setBirthDay(e.target.value)}
                  required
                >
                  <option value="">Day</option>
                  {days.map((day) => (
                    <option key={day} value={day}>{day}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <select
                  id="birthYear"
                  value={birthYear}
                  onChange={(e) => setBirthYear(e.target.value)}
                  required
                >
                  <option value="">Year</option>
                  {years.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="birthday-help" onClick={() => alert('Birthday is used for age verification and to provide personalized services.')}>Help (Birthday)</p>
          </div>

          <div className="form-group">
            <label htmlFor="email">Etheware ID (your email address)</label>
            <input
              type="email"
              id="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <p style={{ fontSize: '13px', color: '#b0b0b0', marginTop: '5px' }}>This will be your new Etheware ID for your Account.</p>
          </div>

          <div className="form-group">
            <label htmlFor="password">Create Your Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>

          <div className="form-group phone-number-section">
            <label htmlFor="phoneNumber">Phone Number</label>
            <div className="phone-input-group">
              <select
                value={phoneCountryCode}
                onChange={(e) => setPhoneCountryCode(e.target.value)}
              >
                {countries.map((c) => (
                  <option key={c.code} value={c.phone}>
                    {c.phone} ({c.name})
                  </option>
                ))}
              </select>
              <input
                type="tel"
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="Enter your phone number"
                required
              />
            </div>
            <p style={{ fontSize: '13px', color: '#b0b0b0', marginTop: '5px' }}>
              Enter a phone number where you can receive verification codes via text or a phone call when signing in.
            </p>
            <div className="verification-methods">
              <label>
                <input
                  type="radio"
                  name="verificationMethod"
                  value="text"
                  checked={verificationMethod === 'text'}
                  onChange={(e) => setVerificationMethod(e.target.value)}
                />
                Text message
              </label>
              <label>
                <input
                  type="radio"
                  name="verificationMethod"
                  value="call"
                  checked={verificationMethod === 'call'}
                  onChange={(e) => setVerificationMethod(e.target.value)}
                />
                Phone call
              </label>
            </div>
          </div>

          <div className="communication-checkboxes">
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="receiveAnnouncements"
                checked={receiveAnnouncements}
                onChange={(e) => setReceiveAnnouncements(e.target.checked)}
              />
              <label htmlFor="receiveAnnouncements">
                **Announcements** Receive Etheware emails and communications including announcements, marketing, recommendations, and updates about Etheware products, services and software.
              </label>
            </div>
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="receiveAppsMusicTV"
                checked={receiveAppsMusicTV}
                onChange={(e) => setReceiveAppsMusicTV(e.target.checked)}
              />
              <label htmlFor="receiveAppsMusicTV">
                **New Apps, Etheware Space Perks, Merchandise, and More** Receive Etheware emails and communications including new releases, exclusive content, special offers, and marketing and recommendations for apps, our satellite internet, and more.
              </label>
            </div>
          </div>

          <div className="captcha-section">
            <div className="captcha-image-wrapper">
              <span className="captcha-image">{captchaImage}</span>
            </div>
            <input
              type="text"
              placeholder="Type the characters in the image"
              value={captchaInput}
              onChange={(e) => setCaptchaInput(e.target.value)}
              required
            />
            <div className="captcha-actions">
              <button type="button" onClick={handleNewCode}>Click to Generate a Code</button>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-button">Continue</button>
          </div>
        </form>

        <p className="privacy-info">
          Your Etheware Account information is used to allow you to sign in securely and access your data. Etheware records certain data for security, support and reporting purposes. If you agree, Etheware may also use your Etheware Account information to send you marketing emails and communications, including based on your use of Etheware services. <a href="/legal/privacy-policy">See how your data is managed.</a>
        </p>
      </div>

    </>
  );
};

export default EthewareSignUp;